import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMoney, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  CloudDownload, RefreshCw, Loader2, Link2Off, FolderOpen, CheckCircle2,
  FileText, Inbox as InboxIcon, Sparkles,
} from "lucide-react";
import { VendorMatchHint } from "@/components/VendorMatchHint";
import { CategoryField } from "@/components/entity/CategoryField";

const SUPER = ["super admin", "admin"];
const CURRENCIES = ["INR", "USD", "EUR", "GBP"];

export default function InvoiceInbox() {
  const { user } = useAuth();
  const isSuper = SUPER.includes(String(user?.role || "").toLowerCase());
  const [params, setParams] = useSearchParams();

  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const [folders, setFolders] = useState(null);
  const [folderOpen, setFolderOpen] = useState(false);
  const [folderSel, setFolderSel] = useState("");
  const [loadingFolders, setLoadingFolders] = useState(false);

  const [review, setReview] = useState(null); // {item, fields, stage}
  const [fields, setFields] = useState({});
  const [parsing, setParsing] = useState(false);
  const [posting, setPosting] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/google-drive/status");
      setStatus(data);
    } catch { /* ignore */ }
  }, []);

  const loadItems = useCallback(async () => {
    try {
      const { data } = await api.get("/drive-inbox");
      setItems(data.items || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadStatus(), loadItems()]);
      setLoading(false);
    })();
  }, [loadStatus, loadItems]);

  useEffect(() => {
    const d = params.get("drive");
    if (!d) return;
    if (d === "connected") toast.success("Google Drive connected");
    else if (d === "norefresh") toast.error("No refresh token — revoke the app in Google and reconnect");
    else if (d) toast.error("Google Drive connection failed — please try again");
    params.delete("drive");
    setParams(params, { replace: true });
    loadStatus();
  }, [params, setParams, loadStatus]);

  const connect = () => {
    window.location.assign(`${API}/google-drive/oauth/start?token=${getToken()}`);
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Drive? Imports will stop until reconnected.")) return;
    await api.post("/google-drive/disconnect");
    toast.success("Disconnected");
    loadStatus();
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/google-drive/sync");
      toast.success(data.imported ? `${data.imported} new bill(s) imported` : "No new bills found");
      await Promise.all([loadStatus(), loadItems()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const openFolderPicker = async () => {
    setFolderOpen(true);
    setLoadingFolders(true);
    try {
      const { data } = await api.get("/google-drive/folders");
      setFolders(data.files || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not list folders");
      setFolders([]);
    } finally {
      setLoadingFolders(false);
    }
  };

  const saveFolder = async () => {
    const f = (folders || []).find((x) => x.id === folderSel);
    if (!f) return toast.error("Pick a folder");
    try {
      const { data } = await api.post("/google-drive/folder", { folder_id: f.id, folder_name: f.name });
      toast.success(data.imported ? `Folder set · ${data.imported} bill(s) imported` : "Folder set");
      setFolderOpen(false);
      await Promise.all([loadStatus(), loadItems()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not set folder");
    }
  };

  const startReview = async (item) => {
    setReview({ item });
    setFields({});
    setParsing(true);
    try {
      const { data } = await api.post(`/drive-inbox/${item.id}/parse`);
      setFields(data.fields || {});
      if (!data.ocr) toast.info("Image invoice — please fill the fields manually");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read invoice");
    } finally {
      setParsing(false);
    }
  };

  const postReview = async () => {
    if (!fields.vendor || !Number(fields.amount)) return toast.error("Vendor and amount are required");
    setPosting(true);
    try {
      await api.post(`/drive-inbox/${review.item.id}/post`, fields);
      toast.success("Payable created");
      setReview(null);
      await Promise.all([loadItems(), loadStatus()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post payable");
    } finally {
      setPosting(false);
    }
  };

  const ignore = async (item) => {
    try {
      await api.post(`/drive-inbox/${item.id}/ignore`);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      loadStatus();
    } catch {
      toast.error("Could not ignore");
    }
  };

  const setF = (k, v) =>
    setFields((p) => {
      if (k === "vendor" && v !== p.vendor) {
        return { ...p, vendor: v, matched_vendor: undefined, vendor_suggestion: undefined, similar_vendors: undefined };
      }
      return { ...p, [k]: v };
    });
  const fileUrl = (att) => `${API}/attachments/${att?.id}/download?auth=${getToken()}`;

  return (
    <div className="space-y-6" data-testid="invoice-inbox-page">
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
          <CloudDownload className="h-6 w-6 text-brand" /> Invoice Inbox
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Bills dropped into your linked Google Drive folder land here for review before they become Payables.
        </p>
      </div>

      {/* Connection panel */}
      <Card className="p-5" data-testid="gdrive-connection">
        {!status?.configured ? (
          <p className="text-sm text-muted-foreground">Google Drive is not configured on the server yet.</p>
        ) : !status?.connected ? (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="font-heading text-lg font-semibold">Connect Google Drive</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Link a Drive account, then pick the folder your team drops invoices into.
              </p>
            </div>
            {isSuper ? (
              <Button onClick={connect} className="gap-2 shrink-0" data-testid="gdrive-connect-btn">
                <CloudDownload className="h-4 w-4" /> Connect Google Drive
              </Button>
            ) : (
              <Badge variant="outline">Super admin connects this</Badge>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span className="font-medium">Connected</span>
                {status.connected_by && <span className="text-muted-foreground">· {status.connected_by}</span>}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={sync} disabled={syncing} className="gap-2" data-testid="gdrive-sync-btn">
                  <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> Sync now
                </Button>
                {isSuper && (
                  <Button size="sm" variant="ghost" onClick={disconnect} className="gap-2 text-muted-foreground hover:text-danger" data-testid="gdrive-disconnect-btn">
                    <Link2Off className="h-4 w-4" /> Disconnect
                  </Button>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="text-muted-foreground">Watched folder:</span>
              {status.folder_name ? (
                <Badge className="bg-brand/10 text-brand border-brand/25" data-testid="gdrive-folder-name">
                  <FolderOpen className="h-3.5 w-3.5 mr-1" /> {status.folder_name}
                </Badge>
              ) : (
                <span className="text-muted-foreground">none selected</span>
              )}
              {isSuper && (
                <Button size="sm" variant="outline" onClick={openFolderPicker} className="gap-2 h-8" data-testid="gdrive-choose-folder-btn">
                  <FolderOpen className="h-4 w-4" /> {status.folder_name ? "Change folder" : "Choose folder"}
                </Button>
              )}
              {status.last_sync && (
                <span className="text-xs text-muted-foreground ml-auto">Last sync: {formatDate(status.last_sync)}</span>
              )}
            </div>
            {status.last_error && (
              <p className="text-xs text-danger">Last error: {status.last_error}</p>
            )}
          </div>
        )}
      </Card>

      {/* Pending items */}
      <Card className="overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <h3 className="font-heading font-semibold flex items-center gap-2">
            <InboxIcon className="h-4 w-4 text-brand" /> Pending bills
            <Badge variant="outline" data-testid="inbox-count">{items.length}</Badge>
          </h3>
        </div>
        {loading ? (
          <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            No pending bills. New files in your Drive folder will appear here automatically.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {items.map((it) => (
              <div key={it.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid="inbox-item">
                <a href={fileUrl(it.attachment)} target="_blank" rel="noreferrer" className="flex items-center gap-2 min-w-0 hover:text-brand" title="Open file">
                  <FileText className="h-4 w-4 text-brand shrink-0" />
                  <span className="truncate text-sm font-medium">{it.name}</span>
                </a>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-muted-foreground hidden sm:inline">{it.imported_at ? formatDate(it.imported_at) : ""}</span>
                  <Button size="sm" onClick={() => startReview(it)} className="gap-1.5 h-8" data-testid="inbox-review-btn">
                    <Sparkles className="h-3.5 w-3.5" /> Review & Post
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => ignore(it)} className="h-8 text-muted-foreground hover:text-danger" data-testid="inbox-ignore-btn">
                    Ignore
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Folder picker dialog */}
      <Dialog open={folderOpen} onOpenChange={setFolderOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Choose Drive folder</DialogTitle>
            <DialogDescription>Only new files added to this folder are imported.</DialogDescription>
          </DialogHeader>
          {loadingFolders ? (
            <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : (
            <Select value={folderSel} onValueChange={setFolderSel}>
              <SelectTrigger data-testid="gdrive-folder-select"><SelectValue placeholder="Select a folder" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {(folders || []).map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFolderOpen(false)}>Cancel</Button>
            <Button onClick={saveFolder} disabled={!folderSel} data-testid="gdrive-folder-save-btn">Use folder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={!!review} onOpenChange={(v) => !v && setReview(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Review invoice</DialogTitle>
            <DialogDescription>Confirm the extracted fields, then post as a Payable. The file stays attached.</DialogDescription>
          </DialogHeader>
          {review && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-lg border border-border overflow-hidden bg-white">
                {(review.item.mime_type || "").includes("pdf") ? (
                  <iframe src={fileUrl(review.item.attachment)} title="invoice" className="w-full h-[420px]" />
                ) : (
                  <img src={fileUrl(review.item.attachment)} alt="invoice" className="w-full max-h-[420px] object-contain" />
                )}
              </div>
              <div>
                {parsing ? (
                  <div className="h-40 flex flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-6 w-6 animate-spin text-brand" /> AI reading invoice…
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <Label className="text-xs">Vendor</Label>
                      <Input value={fields.vendor || ""} onChange={(e) => setF("vendor", e.target.value)} className="mt-1" data-testid="review-vendor" />
                      <VendorMatchHint fields={fields} setFields={setFields} />
                    </div>
                    <div>
                      <Label className="text-xs">Invoice No.</Label>
                      <Input value={fields.invoice_no || ""} onChange={(e) => setF("invoice_no", e.target.value)} className="mt-1" data-testid="review-invoice" />
                    </div>
                    <div>
                      <Label className="text-xs">Category</Label>
                      <div className="mt-1">
                        <CategoryField catType="expense" value={fields.category || ""} onChange={(v) => setF("category", v)} testid="review-category" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Invoice Date</Label>
                      <Input type="date" value={fields.invoice_date || ""} onChange={(e) => setF("invoice_date", e.target.value)} className="mt-1" data-testid="review-date" />
                    </div>
                    <div>
                      <Label className="text-xs">Due Date</Label>
                      <Input type="date" value={fields.due_date || ""} onChange={(e) => setF("due_date", e.target.value)} className="mt-1" data-testid="review-due" />
                    </div>
                    <div>
                      <Label className="text-xs">Amount</Label>
                      <Input type="number" step="0.01" value={fields.amount || 0} onChange={(e) => setF("amount", Number(e.target.value))} className="mt-1" data-testid="review-amount" />
                    </div>
                    <div>
                      <Label className="text-xs">Currency</Label>
                      <select value={fields.currency || "INR"} onChange={(e) => setF("currency", e.target.value)} className="mt-1 w-full h-10 rounded-md bg-background border border-input px-3 text-sm" data-testid="review-currency">
                        {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2 flex items-center justify-end text-xs font-mono text-muted-foreground border-t border-border pt-2">
                      {formatMoney(fields.amount || 0, fields.currency || "INR")}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReview(null)}>Cancel</Button>
            <Button onClick={postReview} disabled={parsing || posting || !fields.vendor} className="gap-2" data-testid="review-post-btn">
              {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Post as Payable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
